/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

import { pick, cloneDeep } from 'lodash';
import { unitToPixels } from 'shared/libs/utils/numbers';
import { BadgeEditorController } from 'shared/libs/providers/badge-editor/controller';
import { docIsUnChanged, modelToDoc, docToModel, getImageFromUrl, sanitizePage, EMPTY_MODEL_PAGE, MultipageOptions, DEFAULT_DOC } from './utils';

import BadgeEditorLayout from './BadgeEditorLayout.vue';
import BadgeEditorPanel from './BadgeEditorPanel.vue';
import PreviewOptions from './preview/PreviewOptions.vue';
import Switcher from 'shared/ui/components/Switcher.vue';
import { createTransparentImage } from 'shared/libs/utils/image';
import { DefaultImage, ToolName } from 'shared/libs/providers/badge-editor/enum';

const __vue_script__ = {
    name: 'BadgeEditor',

    components: {
        BadgeEditorPanel,
        BadgeEditorLayout,
        PreviewOptions,
        Switcher,
    },

    props: {
        event: {
            type: Object,
            required: true
        },

        user: {
            type: Object,
            required: true
        },

        config: {
            type: Object,
            default: () => ({
                mode: 'standalone'
            })
        }
    },

    data() {
        return {
            ToolName,
            assetList: [],
            backSide: false,
            canRedo: false,
            canUndo: false,
            cleanupHooks: [],
            controller: null,
            currentBadgePage: EMPTY_MODEL_PAGE,
            currentPageIndex: 0,
            currentSelection: [],
            currentTool: ToolName.PANNING,
            docInDb: DEFAULT_DOC,
            fullscreen: false,
            isPanelOpen: false,
            model: docToModel(),
            multipageOption: MultipageOptions.SINGLE,
            ready: false,
            tmpNewImage: null,
            zoomValue: 1,
        };
    },

    computed: {
        imageAssets() {
            return this.assetList.filter(asset =>
                ['image/png', 'image/jpeg'].includes(this.$utils.assets.getContentType(asset))
            );
        },
        fontAssets() {
            return this.assetList.filter(asset =>
                ['font/ttf'].includes(this.$utils.assets.getContentType(asset))
            );
        },
        currentPageDimensions() {
            return pick(this.model, ['dpi', 'unit', 'width', 'height', 'userUnit']);
        },
        allowedFonts() {
            return this.fontAssets.map(({ _id }) => _id);
        },
    },

    watch: {
        backSide(back) {
            this.currentPageIndex = back ? 1 : 0;
            this.currentBadgePage = this.model.pages[this.currentPageIndex] || EMPTY_MODEL_PAGE;
            this.controller.replaceState(this.currentBadgePage);
        },
        multipageOption(option) {
            this.controller.updateState((state) => {
                state.mirrored = option === MultipageOptions.MIRRORED;
                if (option === MultipageOptions.DOUBLE && this.model.pages.length < 2) {
                    this.model.pages.push(EMPTY_MODEL_PAGE);
                }
            });

            if (option !== MultipageOptions.DOUBLE) {
                this.backSide = false;
            }
        },
    },

    async mounted() {
        await this.loadDesign();

        this.controller = new BadgeEditorController(this.$refs.canvas, this.$refs.wrapper, this.currentBadgePage);

        await this.initAssets();

        this.ready = true;

        // needs to be done after initializing assets
        this.controller.syncState();

        this.controller.addEventListener('zoom', (zoom) => {
            this.zoomValue = zoom;
        });

        this.controller.addEventListener('selection', selection => {
            // `selection` is either an array of fabric objects or the string 'background'
            if (selection === 'background') {
                this.currentSelection = [];
                this.isPanelOpen = true;
                this.currentTool = ToolName.BACKGROUND;
                return;
            } else {
                this.currentSelection = selection;
                this.isPanelOpen = selection.length > 0;
                if (this.isPanelOpen) {
                    const tool = selection.at(0);
                    if (tool?.isQrCode) {
                        this.currentTool = ToolName.QR_CODE;
                        return;
                    }
                    if (tool?.isImage) {
                        this.currentTool = ToolName.IMAGE;
                        return;
                    }
                    this.currentTool = ToolName.TEXT_BOX;
                    return;
                }
            }
            this.currentTool = ToolName.PANNING;
        });

        this.controller.state.addEventListener('state', (state) => {
            const page = sanitizePage(state, true);
            this.model.pages[this.currentPageIndex] = page;
            this.currentBadgePage = page;
            this.canRedo = this.controller.state.canRedo;
            this.canUndo = this.controller.state.canUndo;
        });

        this.handleEditorLayout();

        if (this.model?.mirrored) {
            this.multipageOption = MultipageOptions.MIRRORED;
        } else if (this.model?.pages?.length > 1) {
            this.multipageOption = MultipageOptions.DOUBLE;
        }

        this.cleanupHooks = [
            ...this.cleanupHooks,
            this.setUpWarnOnUnsavedChanges(),
        ];
    },

    beforeDestroy() {
        for (const hook of this.cleanupHooks) {
            hook();
        }
    },

    unmounted() {
        this.controller?.dispose();
    },

    methods: {
        async initAssets() {
            const { items } = await this.$services.documents.getAllDocsByType(this.event._id, this.$services.documents.PRINTABLE_ASSET_TYPE);
            this.assetList = items;
            await this.loadAssets();
        },

        docHasNotChanged() {
            const doc = modelToDoc(this.model, this.allowedFonts, this.multipageOption);
            return docIsUnChanged(doc, this.docInDb);
        },

        setUpWarnOnUnsavedChanges() {
            // the router is in angular, this vue "Page" is rendered within an angular "app"
            const angularRootScope = angular.element(document.body).scope().$root;
            if (angularRootScope && angularRootScope.$on) {
                // angular does not have an $off method or any other removeEventListener equivalent
                // it returns a function to call to remove the event listener
                const removeRouterListener = angularRootScope.$on('$routeChangeStart', (event) => {
                    if (this.docHasNotChanged()) {
                        return removeRouterListener();
                    }
                    const confirmed = window.confirm(this.$t('onsite_scanning.badge_editor.warn_unsaved_changes'));
                    if (event && !confirmed) {
                        event.preventDefault(); // prevents you from leaving the page
                    } else {
                        removeRouterListener();
                    }
                });
            }

            const browserCloseHandler = (event) => {
                if (this.docHasNotChanged()) {
                    return;
                }

                event.returnValue = ''; // required for chrome
                event.preventDefault(); // prevents you from leaving the page

                window.confirm(this.$t('onsite_scanning.badge_editor.warn_unsaved_changes'));
            };

            window.addEventListener('beforeunload', browserCloseHandler);

            return () => {
                window.removeEventListener('beforeunload', browserCloseHandler);
            };
        },

        async handleEditorLayout(value) {
            if (value) {
                Object.assign(this.model, value);
            }
            this.controller.updateBackdropDimensions(
                unitToPixels(this.model.width, this.model.dpi, this.model.unit),
                unitToPixels(this.model.height, this.model.dpi, this.model.unit),
            );
        },

        async loadAssets() {
            // cache all images in assets list
            for (const assetDoc of this.imageAssets) {
                await this.cacheImage(assetDoc);
            }

            // dummy QR code
            await this.cacheQrCodeImage();

            // image placeholder
            this.cacheImagePlaceholder();

            // fonts
            for (const assetDoc of this.fontAssets) {
                await this.$utils.dom.loadFontFace(assetDoc._id, this.getAssetUrl(assetDoc));
            }
        },

        /** @type {(assetDoc: Record<string,unknown>) => string} */
        getAssetUrl(assetDoc) {
            if (assetDoc.src) return assetDoc.src;
            const [filename] = Object.keys(assetDoc.fp_attachments_s3 || { [assetDoc.file_name]: assetDoc.file_name });
            return this.$utils.assets.getEventAttachmentPath(this.event._id, filename, assetDoc._id);
        },

        async cacheQrCodeImage() {
            try {
                const image = await getImageFromUrl(this.$utils.assets.imagePath('qr-code.svg'));
                this.controller.cacheImage('qrcode', image);
            } catch (error) {
                console.error('[BadgeEditor] could not cache dummy qr code', error);
            }
        },

        cacheImagePlaceholder() {
            try {
                const image = createTransparentImage(DefaultImage.width, DefaultImage.height);
                this.controller.cacheImage('placeholder', image);
            } catch (error) {
                console.error('[BadgeEditor] could not cache image placeholder', error);
            }
        },

        /** @param {Record<string,unknown>} assetDoc */
        async cacheImage(assetDoc) {
            try {
                const image = await getImageFromUrl(this.getAssetUrl(assetDoc));
                this.controller.cacheImage(assetDoc._id, image);
            } catch (error) {
                console.error(`[BadgeEditor] could not cache image from asset ${assetDoc?._id}`, error);
            }
        },

        async loadDesign() {
            try {
                const { eventId, modelId } = this.$router.getRouteParams() || {};
                if (!modelId) {
                    return;
                }

                const doc = await this.$services.documents.getDocById(eventId, modelId);
                this.docInDb = cloneDeep(doc);
                this.model = docToModel(doc);
                this.currentBadgePage = this.model.pages.at(0);
            } catch (error) {
                console.error('[BadgeEditor] Could not load model', error);
            }
        },

        async save(formData) {
            try {
                console.debug('[BadgeEditor] Saving design', formData);
                const isValid = await this.$refs.layout.validate();
                if (!isValid) {
                    return;
                }

                const { _id: eventId } = this.event;
                const node = this.$utils.workspaces.getRootNodeName(this.event);
                const fpType = this.config.modelFpType;
                const doc = modelToDoc(formData, this.allowedFonts, this.multipageOption);
                const { data } = await this.$services.documents.updateDoc(eventId, node, fpType, doc);
                const [modelId] = data.upsertedDocIds;
                this.docInDb = await this.$services.documents.getDocById(eventId, modelId);

                console.info('[BadgeEditor] Successfully saved', doc, data);

                if (formData._id) {
                    // ...if it's an existing design, reload the model after save
                    // so that the WYSIWYG between UI and BE data is honored
                    await this.controller.syncState();

                } else {
                    // If it's a new design, redirect the user to the proper detailed page after saving
                    let path;
                    if (fpType === 'badge_design') {
                        const { route } = this.$services.blueprint.getControllerByName('badge-design', 'view');
                        path = route.at(0)
                            .replace(':eventId', eventId)
                            .replace(':modelId', modelId);
                    }

                    // set model in order to pass docIsUnChanged checks
                    this.model = docToModel(this.docInDb);

                    this.$services.router.redirect(path);
                }

                this.notify('onsite_scanning.badge_editor.save_successful');

            } catch (error) {
                console.error('[BadgeEditor] Could not save layout', error);
                this.notify('onsite_scanning.badge_editor.save_failed', 'error', 10000);
            }
        },

        notify(key, type = 'success', duration = 5000) {
            this.$notify({
                group: 'flashes',
                text: this.$t(key).toString(),
                type,
                duration,
            });
        },

        closePanels() {
            this.isPanelOpen = false;
            this.currentSelection = [];
            this.onToolChange(ToolName.PANNING);
        },

        /**
         * @param {object} args
         * @param {string} args._id
         * @param {'text'|'qrcode'|'image'} args.type
         * @param {Record<string,string|number>} args.data
         */
        onObjectChange({ _id, type, data }) {
            this.controller.updateElement(_id, type, data);
        },

        /** @param {string} backgroundId */
        onBackgroundChange(backgroundId) {
            this.controller.updateBackgroundImage(backgroundId);
        },

        /** @param {{ id: string, imageId: string }} */
        onImageChange({ id, imageId }) {
            this.controller.updateImage({ id, imageId });
        },

        /** @param {{ _id: string, type: 'text'|'qrcode'|'image' }} objectToDuplicate */
        onDuplicateObject({ _id, type }) {
            this.controller.duplicateElement(_id, type);
        },

        /** @param {{ _id: string, type: 'text'|'qrcode'|'image' }} objectToRemove */
        onRemoveObject({ _id, type }) {
            this.controller.removeElement(_id, type);
            this.onToolChange(ToolName.PANNING);
        },

        /** @param {ToolName} tool */
        onToolChange(tool) {
            this.currentTool = tool;
            this.controller.setTool(tool);

            // open background image panel if user clicks "background"
            if (tool === ToolName.BACKGROUND) {
                this.isPanelOpen = true;
            }
        }
    }
};

/* template */
var __vue_render__ = function () {
  var _vm = this
  var _h = _vm.$createElement
  var _c = _vm._self._c || _h
  return _c(
    "div",
    {
      staticClass: "badge-editor-parent",
      style: { opacity: _vm.ready ? 1 : 0 },
    },
    [
      _c("badge-editor-layout", {
        ref: "layout",
        attrs: { event: _vm.event, user: _vm.user, config: _vm.config },
        on: { input: _vm.handleEditorLayout },
        model: {
          value: _vm.model,
          callback: function ($$v) {
            _vm.model = $$v
          },
          expression: "model",
        },
      }),
      _c("div", { staticClass: "badge-editor-multipage" }, [
        _c(
          "button",
          {
            staticClass: "multipage-option",
            class: { active: _vm.multipageOption === "single" },
            on: {
              click: function ($event) {
                _vm.multipageOption = "single"
              },
            },
          },
          [
            _c("i", { staticClass: "icon ic-single-page" }),
            _c("span", [_vm._v("Single sided")]),
          ]
        ),
        _c(
          "button",
          {
            staticClass: "multipage-option",
            class: { active: _vm.multipageOption === "double" },
            on: {
              click: function ($event) {
                _vm.multipageOption = "double"
              },
            },
          },
          [
            _c("i", { staticClass: "icon ic-double-page" }),
            _c("span", [_vm._v("Double sided")]),
          ]
        ),
        _c(
          "button",
          {
            staticClass: "multipage-option",
            class: { active: _vm.multipageOption === "mirrored" },
            on: {
              click: function ($event) {
                _vm.multipageOption = "mirrored"
              },
            },
          },
          [
            _c("i", { staticClass: "icon ic-double-page-mirrored" }),
            _c("span", [_vm._v("Double sided - mirrored")]),
          ]
        ),
      ]),
      _c(
        "div",
        {
          ref: "container",
          staticClass: "badge-editor",
          class: { fullscreen: _vm.fullscreen },
        },
        [
          _c(
            "div",
            { staticClass: "toolbar" },
            [
              _c("div", { staticClass: "tools" }, [
                _c(
                  "button",
                  {
                    staticClass: "tool",
                    class: {
                      active: _vm.currentTool === _vm.ToolName.BACKGROUND,
                    },
                    on: {
                      click: function ($event) {
                        return _vm.onToolChange(_vm.ToolName.BACKGROUND)
                      },
                    },
                  },
                  [_c("i", { staticClass: "icon ic-background-element" })]
                ),
                _c(
                  "button",
                  {
                    staticClass: "tool",
                    class: {
                      active: _vm.currentTool === _vm.ToolName.TEXT_BOX,
                    },
                    on: {
                      click: function ($event) {
                        return _vm.onToolChange(_vm.ToolName.TEXT_BOX)
                      },
                    },
                  },
                  [_c("i", { staticClass: "icon ic-text-element" })]
                ),
                _c(
                  "button",
                  {
                    staticClass: "tool",
                    class: { active: _vm.currentTool === _vm.ToolName.QR_CODE },
                    on: {
                      click: function ($event) {
                        return _vm.onToolChange(_vm.ToolName.QR_CODE)
                      },
                    },
                  },
                  [_c("i", { staticClass: "icon ic-qr-code-element" })]
                ),
                _c(
                  "button",
                  {
                    staticClass: "tool",
                    class: { active: _vm.currentTool === _vm.ToolName.IMAGE },
                    on: {
                      click: function ($event) {
                        return _vm.onToolChange(_vm.ToolName.IMAGE)
                      },
                    },
                  },
                  [_c("i", { staticClass: "icon ic-image-element" })]
                ),
                _c(
                  "button",
                  {
                    staticClass: "tool",
                    class: { active: _vm.currentTool === _vm.ToolName.PANNING },
                    on: {
                      click: function ($event) {
                        return _vm.onToolChange(_vm.ToolName.PANNING)
                      },
                    },
                  },
                  [_c("i", { staticClass: "icon ic-hand-tool" })]
                ),
                _c("div", { staticClass: "zoom" }, [
                  _c(
                    "button",
                    {
                      on: {
                        click: function ($event) {
                          return _vm.controller.setZoom(_vm.zoomValue - 0.1)
                        },
                      },
                    },
                    [_c("i", { staticClass: "icon ic-minus" })]
                  ),
                  _c("span", [
                    _vm._v(_vm._s((_vm.zoomValue * 100).toFixed(0)) + "%"),
                  ]),
                  _c(
                    "button",
                    {
                      on: {
                        click: function ($event) {
                          return _vm.controller.setZoom(_vm.zoomValue + 0.1)
                        },
                      },
                    },
                    [_c("i", { staticClass: "icon ic-plus" })]
                  ),
                ]),
              ]),
              _c("div", { staticClass: "spacer" }),
              _vm.multipageOption === "double"
                ? _c("switcher", {
                    attrs: {
                      choices: [
                        { label: "Front", value: false },
                        { label: "Back", value: true },
                      ],
                    },
                    model: {
                      value: _vm.backSide,
                      callback: function ($$v) {
                        _vm.backSide = $$v
                      },
                      expression: "backSide",
                    },
                  })
                : _vm._e(),
              _c("div", { staticClass: "spacer" }),
              _c("div", { staticClass: "actions" }, [
                _c(
                  "button",
                  {
                    staticClass: "action",
                    attrs: { disabled: !_vm.canUndo },
                    on: {
                      click: function ($event) {
                        return _vm.controller.undo()
                      },
                    },
                  },
                  [_c("i", { staticClass: "icon ic-undo" })]
                ),
                _c(
                  "button",
                  {
                    staticClass: "action",
                    attrs: { disabled: !_vm.canRedo },
                    on: {
                      click: function ($event) {
                        return _vm.controller.redo()
                      },
                    },
                  },
                  [_c("i", { staticClass: "icon ic-redo" })]
                ),
                _c(
                  "button",
                  {
                    staticClass: "action",
                    on: {
                      click: function ($event) {
                        _vm.fullscreen = !_vm.fullscreen
                      },
                    },
                  },
                  [
                    _c("i", {
                      class: {
                        "ic-expand": !_vm.fullscreen,
                        "ic-shrink": _vm.fullscreen,
                      },
                    }),
                  ]
                ),
              ]),
            ],
            1
          ),
          _c(
            "div",
            { staticClass: "main" },
            [
              _c("badge-editor-panel", {
                attrs: {
                  "current-badge-page": _vm.currentBadgePage,
                  "current-selection": _vm.currentSelection,
                  "current-page-dimensions": _vm.currentPageDimensions,
                  event: _vm.event,
                  "font-assets": _vm.fontAssets,
                  "image-assets": _vm.imageAssets,
                  open: _vm.isPanelOpen,
                  preset: _vm.model.preset,
                },
                on: {
                  "image-change": _vm.onImageChange,
                  "background-change": _vm.onBackgroundChange,
                  "close-panel": function ($event) {
                    return _vm.closePanels()
                  },
                  "object-change": _vm.onObjectChange,
                  "remove-object": _vm.onRemoveObject,
                  "duplicate-object": _vm.onDuplicateObject,
                },
              }),
              _c("div", { ref: "wrapper", staticClass: "wrapper" }, [
                _c("canvas", { ref: "canvas" }),
              ]),
            ],
            1
          ),
          _c(
            "secondary-toolbar",
            [
              _vm.ready
                ? _c("preview-options", {
                    attrs: {
                      event: _vm.event,
                      "badge-model": _vm.model,
                      "image-cache": _vm.controller.imageCache,
                      "multi-page": _vm.multipageOption,
                    },
                  })
                : _vm._e(),
              _c(
                "button",
                {
                  staticClass: "btn btn-primary",
                  on: {
                    click: function ($event) {
                      return _vm.save(_vm.model)
                    },
                  },
                },
                [_vm._v(_vm._s(_vm.$t("general.save")))]
              ),
            ],
            1
          ),
        ],
        1
      ),
    ],
    1
  )
}
var __vue_staticRenderFns__ = []
__vue_render__._withStripped = true

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "apps/shared/src/ui/components/editors/badge-editor/BadgeEditor.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__